import { Box, CircularProgress, Grid, TextField } from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'
import {
    ReactElement,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { fetchUserOrganizations } from '../../../services/userServices'
import { IOrganization, IOrgDropdown } from './interfaces'
import { useTranslation } from 'react-i18next'
import { GetUserOrganizations } from '../../../services/apiInterfaces'
import useDebounce from '../../../CustomHooks/useDebounce'
import type {
    AutocompleteInputChangeReason,
    AutocompleteRenderInputParams,
} from '@mui/material/Autocomplete'
import { useAuth0 } from '@auth0/auth0-react'
import { useUnsavedChanges } from '../../../contexts/UnsavedChangesProvider'
import { useAppDispatch } from '../../../redux/hook'
import { setHasNoOrganizations } from '../../../redux/slices/orgSlice'
import { updateLoader } from '../../../redux/slices/loaderSlice'

export const OrgDropdown = (props: IOrgDropdown): ReactElement => {
    const { t } = useTranslation()
    const { guard } = useUnsavedChanges()
    const dispatch = useAppDispatch()
    const [search, setSearch] = useState('')
    const { isAuthenticated } = useAuth0()
    const debouncedSearch = useDebounce(search, 400)
    const [selectedOrg, setSelectedOrg] = useState<IOrganization | null>(null)
    const hasSetFirstOrg = useRef(false)
    const pageSize = 30

    const storedDefaultOrg = localStorage.getItem('DEFAULT_ORG')
    let parsedOrg: IOrganization | null = null
    if (storedDefaultOrg) {
        try {
            parsedOrg = JSON.parse(storedDefaultOrg)
        } catch (e) {
            console.log(e)
        }
    }

    const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
        useInfiniteQuery<GetUserOrganizations, unknown>({
            queryKey: ['organizations', debouncedSearch],
            queryFn: async (context) =>
                fetchUserOrganizations({
                    pageNumber:
                        typeof context.pageParam === 'number'
                            ? context.pageParam
                            : 1,
                    size: pageSize,
                    currentOrgId: parsedOrg?.id,
                    searchText: debouncedSearch?.trim(),
                }),
            initialPageParam: 1,
            getNextPageParam: (
                lastPage: GetUserOrganizations,
                allPages: GetUserOrganizations[],
            ) => {
                const loaded = allPages.flatMap((p) => p.organizations).length
                return loaded < lastPage.totalCount
                    ? allPages.length + 1
                    : undefined
            },
            refetchOnWindowFocus: false,
            enabled: isAuthenticated,
        })

    const orgsList = useMemo(() => {
        const orgs = data?.pages.flatMap((p) => p.organizations) || []
        // Add load more option if there are more pages
        if (hasNextPage) {
            orgs.push({
                id: 'load-more',
                name: isFetchingNextPage ? t('loading') : 'Load More...',
                isEnterpriseOrg: false,
            } as IOrganization)
        }
        return orgs
    }, [t, data, hasNextPage, isFetchingNextPage])

    const updateOrg = useCallback(
        (selectedOrgName: IOrganization | null) => {
            if (!selectedOrgName) {
                return
            }
            guard(() => {
                setSelectedOrg(selectedOrgName)
                props.updateOrg(selectedOrgName, true)
            })
        },
        [props, guard],
    )

    useEffect(() => {
        if (orgsList.length > 0 && !hasSetFirstOrg.current) {
            hasSetFirstOrg.current = true
            // updateOrg(orgsList[0])
            setSelectedOrg(orgsList[0])
            /**
             * API ensure that the currentOrgId (i.e., parsedOrg?.id) is returned as the first object in the response. Hence the below condition.
             * If the user lost the access to currentOrgId, then it will not be returned. As a result we will update the org.
             */
            const isUpdatingOrg = false //During the intial load of application we set org automatically. Hence no org change message.
            props.updateOrg(orgsList[0], isUpdatingOrg)
        }
    }, [orgsList, props])

    useEffect(() => {
        if (!isAuthenticated || isLoading || isFetchingNextPage || !data) {
            return
        }
        const hasSearch = Boolean(debouncedSearch?.trim())
        const totalCount = data.pages?.[0]?.totalCount ?? 0
        if (!hasSearch && totalCount === 0) {
            dispatch(setHasNoOrganizations(true))
            dispatch(updateLoader(false))
        } else if (totalCount > 0) {
            dispatch(setHasNoOrganizations(false))
        }
    }, [
        isAuthenticated,
        isLoading,
        isFetchingNextPage,
        data,
        debouncedSearch,
        dispatch,
    ])

    const handleChange = useCallback(
        (_event: React.SyntheticEvent, newValue: any) => {
            if (newValue?.id !== 'load-more') {
                updateOrg(newValue)
                /**UBQ-2397 Fix Starts here */
                setSearch('') //This will reset the menu options
                /**UBQ-2397 Fix Ends here */
            }
        },
        [updateOrg],
    )

    const handleInputChange = useCallback(
        (
            _event: React.SyntheticEvent,
            value: string,
            reason: AutocompleteInputChangeReason,
        ) => {
            if (reason === 'input' || reason === 'clear') {
                setSearch(value)
            } else if (reason === 'blur') {
                /**UBQ-2397 Fix Starts here */
                setSearch('') //This will reset the menu options
                /**UBQ-2397 Fix Ends here */
            }
        },
        [],
    )

    // Infinite scroll handler
    const handleListboxScroll = useCallback(
        (event: React.UIEvent<HTMLUListElement>) => {
            const listboxNode = event.currentTarget as HTMLUListElement
            if (
                listboxNode.scrollTop + listboxNode.clientHeight >=
                    listboxNode.scrollHeight - 30 &&
                hasNextPage &&
                !isFetchingNextPage
            ) {
                fetchNextPage()
            }
        },
        [hasNextPage, isFetchingNextPage, fetchNextPage],
    )

    return (
        <Grid container>
            <Autocomplete
                id="navbar-org-select"
                disablePortal
                options={orgsList}
                getOptionLabel={(option) => option.name}
                renderOption={(
                    props: React.HTMLAttributes<HTMLLIElement>,
                    option: IOrganization,
                ) => {
                    if (option.id === 'load-more') {
                        return (
                            <Box
                                component="li"
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'primary.main',
                                    fontStyle: 'italic',
                                }}
                                {...props}
                                onClick={() =>
                                    !isFetchingNextPage && fetchNextPage()
                                }
                            >
                                {isFetchingNextPage && (
                                    <CircularProgress
                                        size={16}
                                        sx={{ mr: 1 }}
                                    />
                                )}
                                {option.name}
                            </Box>
                        )
                    }
                    return (
                        <Box
                            component="li"
                            sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                            {...props}
                        >
                            {option.name}
                            {option.isEnterpriseOrg ? (
                                <img
                                    src={`/assets/images/icons/e-icon.svg`}
                                    alt="Enterprise"
                                    style={{
                                        marginLeft: '5px',
                                        marginTop: '-10px',
                                        width: '15px',
                                        borderRadius: '25px',
                                    }}
                                />
                            ) : null}
                        </Box>
                    )
                }}
                slotProps={{
                    clearIndicator: {
                        id: 'navbar-org-select-clear-button',
                    },
                    popupIndicator: {
                        id: 'navbar-org-select-dropdown-button',
                    },
                }}
                value={selectedOrg}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                sx={{
                    width: '95%',
                    backgroundColor: '#ffffff',
                    borderRadius: '8px',
                    '& .MuiOutlinedInput-root': {
                        backgroundColor: '#ffffff',
                        padding: '2.5px 4px 2.5px 5px',
                    },
                    // ensure listbox UL has constrained height + scroll
                    '& .MuiAutocomplete-listbox': {
                        maxHeight: 300,
                        overflow: 'auto',
                    },
                }}
                onChange={handleChange}
                onInputChange={handleInputChange}
                loading={isLoading || isFetchingNextPage}
                loadingText={
                    <Box
                        component="li"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            // justifyContent: 'center',
                            color: 'primary.main',
                            fontStyle: 'italic',
                        }}
                        // {...props}
                    >
                        {<CircularProgress size={16} sx={{ mr: 1 }} />}
                        {t('loading')}
                    </Box>
                }
                ListboxProps={{
                    onScroll: handleListboxScroll,
                    style: { maxHeight: 300, overflow: 'auto' },
                }}
                renderInput={(params: AutocompleteRenderInputParams) => (
                    <TextField
                        {...params}
                        placeholder={t('chooseAnOrganization')}
                        aria-label={t('chooseAnOrganization')}
                    />
                )}
                noOptionsText={t('noOrganizationsFound')}
            />
        </Grid>
    )
}
